import { useEffect, useMemo, useRef, useState } from "react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import ImpactMap from "@/components/ImpactMap";
import ImpactCounters from "@/components/ImpactCounters";
import MapFilters from "@/components/MapFilters";
import AccessibleNeighborhoodTable from "@/components/AccessibleNeighborhoodTable";
import ShareCardModal from "@/components/ShareCardModal";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/api";
import { FAMILY_PORTAL_URL } from "@/lib/links";
import { ArrowRight, BookOpen, Heart, MapPinned, Sprout } from "lucide-react";
import { LANDING } from "@/constants/testIds";

const REFRESH_MS = 30_000;
const HERO_IMAGE_URL = "https://customer-assets.emergentagent.com/job_library-locator-2/artifacts/lbu9qghy_TBP%20Children%20Reading.webp";

export default function Landing() {
  const [families, setFamilies] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    jurisdiction: "ALL",
    ward: "ALL",
    neighborhood: "",
    from_date: "",
    to_date: "",
  });
  const [layers, setLayers] = useState({ pending: true, scheduled: true, active: true });
  const [showLiteracyLayer, setShowLiteracyLayer] = useState(true);
  const [showMdLiteracyLayer, setShowMdLiteracyLayer] = useState(false);
  const [showVaLiteracyLayer, setShowVaLiteracyLayer] = useState(false);
  const [showDcplLayer, setShowDcplLayer] = useState(false);
  const lastActiveIdsRef = useRef(new Set());
  const [newlyInstalledIds, setNewlyInstalledIds] = useState(new Set());
  const [shareFamily, setShareFamily] = useState(null);

  // Build the query params for /families/public
  const params = useMemo(() => {
    const p = {};
    if (filters.jurisdiction !== "ALL") p.jurisdiction = filters.jurisdiction;
    if (filters.ward !== "ALL") p.ward = filters.ward;
    if (filters.neighborhood.trim()) p.neighborhood = filters.neighborhood.trim();
    if (filters.from_date) p.from_date = filters.from_date;
    if (filters.to_date) p.to_date = filters.to_date;
    return p;
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    let firstLoad = lastActiveIdsRef.current.size === 0;

    async function load() {
      try {
        const [fRes, sRes] = await Promise.all([
          apiClient.get("/families/public", { params }),
          apiClient.get("/stats/public"),
        ]);
        if (cancelled) return;
        const list = fRes.data || [];
        setFamilies(list);
        setStats(sRes.data || null);

        // Detect newly-installed pins between polls.
        const activeIds = new Set(list.filter((f) => f.status === "active").map((f) => f.id));
        if (!firstLoad) {
          const fresh = new Set();
          activeIds.forEach((id) => { if (!lastActiveIdsRef.current.has(id)) fresh.add(id); });
          if (fresh.size > 0) {
            setNewlyInstalledIds(fresh);
            setTimeout(() => setNewlyInstalledIds(new Set()), 2200);
          }
        }
        lastActiveIdsRef.current = activeIds;
        firstLoad = false;
      } catch (e) {
        // silent — keep last data
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [params]);

  return (
    <div className="App min-h-screen flex flex-col">
      <SiteHeader />

      {/* HERO */}
      <section className="warm-grain border-b border-stone-200/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-14 sm:pt-24 sm:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-stone-200 text-xs font-semibold tracking-wider text-[#122036] uppercase mb-6">
              <Sprout size={14} className="text-[#3DA9DC]" />
              DMV community impact
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#122036]">
              Every child <span className="text-[#3DA9DC]">deserves a shelf</span> of books to call their own.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-stone-700 leading-relaxed max-w-2xl">
              We deliver and install bookshelves, filled with carefully chosen children's
              books, into homes across Washington D.C., Maryland, and Virginia. Watch the map
              fill up, neighborhood by neighborhood.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href={FAMILY_PORTAL_URL} target="_blank" rel="noreferrer">
                <Button
                  data-testid={LANDING.heroCtaRegister}
                  className="bg-[#122036] hover:bg-[#0B1626] text-white rounded-full px-6 py-6 text-base shadow-sm"
                >
                  Request a library <ArrowRight size={16} className="ml-1.5" />
                </Button>
              </a>
              <a href="#impact-map">
                <Button
                  data-testid={LANDING.heroCtaLearn}
                  variant="outline"
                  className="rounded-full border-[#122036]/15 text-[#122036] px-6 py-6 text-base hover:bg-[#122036]/5"
                >
                  See the map
                </Button>
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="rounded-3xl overflow-hidden border border-stone-200 shadow-[0_20px_60px_rgba(18,32,54,0.18)] aspect-[4/3] bg-[#F4EFE6]">
                <img
                  src={HERO_IMAGE_URL}
                  alt="A family reading together at home with a full bookshelf — the joy a Bookshelf Project home library brings."
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl border border-stone-200 shadow-lg px-5 py-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#22C55E] text-white grid place-items-center">
                  <BookOpen size={18} />
                </div>
                <div>
                  <div className="font-serif text-2xl font-semibold leading-none text-[#122036]">
                    {(stats?.total_installed || 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-stone-500 uppercase tracking-wider mt-1">Libraries installed</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COUNTERS */}
      <section className="max-w-7xl mx-auto w-full px-6 lg:px-8 mt-14 sm:mt-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#3DA9DC] font-semibold">Live impact snapshot</div>
            <h2 className="font-serif text-3xl sm:text-4xl mt-2 text-[#122036]">Where the books are going</h2>
          </div>
          <p className="text-sm text-stone-600 max-w-md">
            Updated automatically every 30 seconds as families register and deliveries complete.
          </p>
        </div>
        <ImpactCounters stats={stats} />
      </section>

      {/* MAP — big centered title so visitors know the page's purpose */}
      <section
        id="impact-map"
        data-testid={LANDING.mapSection}
        className="max-w-7xl mx-auto w-full px-6 lg:px-8 mt-16 sm:mt-24"
      >
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-stone-200 text-xs font-semibold tracking-[0.18em] text-[#3DA9DC] uppercase mb-5">
            <MapPinned size={14} />
            Live across the DMV
          </div>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-[#122036] leading-none">
            Impact Map
          </h2>
          <p className="mt-5 max-w-2xl mx-auto text-base sm:text-lg text-stone-600 leading-relaxed">
            Every pin is a home library. Watch the DMV light up, neighborhood by
            neighborhood, as families register and our team delivers shelves filled with books.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-3 order-2 lg:order-1">
            <MapFilters
              filters={filters}
              setFilters={setFilters}
              layers={layers}
              setLayers={setLayers}
              showLiteracyLayer={showLiteracyLayer}
              setShowLiteracyLayer={setShowLiteracyLayer}
              showMdLiteracyLayer={showMdLiteracyLayer}
              setShowMdLiteracyLayer={setShowMdLiteracyLayer}
              showVaLiteracyLayer={showVaLiteracyLayer}
              setShowVaLiteracyLayer={setShowVaLiteracyLayer}
              showDcplLayer={showDcplLayer}
              setShowDcplLayer={setShowDcplLayer}
            />
          </div>
          <div className="lg:col-span-9 order-1 lg:order-2">
            <ImpactMap
              families={families}
              layers={layers}
              newlyInstalledIds={newlyInstalledIds}
              onShare={setShareFamily}
              showLiteracyLayer={showLiteracyLayer}
              showMdLiteracyLayer={showMdLiteracyLayer}
              showVaLiteracyLayer={showVaLiteracyLayer}
              setShowVaLiteracyLayer={setShowVaLiteracyLayer}
              showDcplLayer={showDcplLayer}
            />
            <div className="mt-3 flex items-center justify-between text-xs text-stone-500 flex-wrap gap-2">
              <span>Showing {families.length.toLocaleString()} {families.length === 1 ? "family" : "families"} · pin locations randomly offset to protect privacy.</span>
              <span>Zoom in past Ward level to see individual installations.</span>
            </div>
          </div>
        </div>
      </section>

      <ShareCardModal
        family={shareFamily}
        open={!!shareFamily}
        onOpenChange={(v) => !v && setShareFamily(null)}
      />

      {/* ACCESSIBLE TABLE */}
      <section className="max-w-7xl mx-auto w-full px-6 lg:px-8 mt-10">
        <AccessibleNeighborhoodTable neighborhoods={stats?.neighborhoods || []} />
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto w-full px-6 lg:px-8 mt-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { n: "01", title: "Family registers", body: "Share a few details so we can plan a delivery to your home." , icon: Heart, color: "#F4A6A6" },
            { n: "02", title: "We assemble the library", body: "Custom bookshelf + a curated set of age-appropriate books.", icon: BookOpen, color: "#3DA9DC" },
            { n: "03", title: "Delivered & installed", body: "Our team installs the shelf and adds a green pin to the map.", icon: Sprout, color: "#22C55E" },
          ].map((s) => (
            <div key={s.n} className="bg-white rounded-2xl border border-stone-200 p-7 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold">{s.n}</span>
                <span className="w-9 h-9 rounded-full grid place-items-center" style={{ background: s.color + "1A", color: s.color }}>
                  <s.icon size={18} />
                </span>
              </div>
              <h3 className="font-serif text-xl mt-4 text-[#122036]">{s.title}</h3>
              <p className="text-sm text-stone-600 mt-2 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
